"use client";

import { GAME_PLAYER_READY_FADE_MS } from "@/lib/constants/layout";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

export function useFadeInOnReady(ready: boolean) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ready) {
      setVisible(false);
      return;
    }

    const frameId = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frameId);
  }, [ready]);

  return {
    fadeClassName: cn("transition-opacity ease-out", visible ? "opacity-100" : "opacity-0"),
    fadeStyle: { transitionDuration: `${GAME_PLAYER_READY_FADE_MS}ms` },
  };
}

/** Keeps the loading overlay mounted briefly so it can fade out. */
export function useLoadingOverlayExit(loading: boolean) {
  const [mounted, setMounted] = useState(loading);
  const [visible, setVisible] = useState(loading);

  useEffect(() => {
    if (loading) {
      setMounted(true);
      const frameId = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frameId);
    }

    if (!mounted) {
      return;
    }

    setVisible(false);
    const timeoutId = window.setTimeout(() => setMounted(false), GAME_PLAYER_READY_FADE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [loading, mounted]);

  return { overlayMounted: mounted, overlayVisible: visible };
}
